/**
 * AI 自動スケジュール ダイアログ
 *
 * モード選択 + 期間設定 + カスタム指示を設定して実行する
 */

import { format } from 'date-fns'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

type ScheduleMode = 'unscheduled' | 'reschedule_all' | 'reschedule_range'

type Props = {
  open: boolean
  onClose: () => void
  onSubmit: (params: {
    mode: ScheduleMode
    rangeStart?: string
    rangeEnd?: string
    customInstructions?: string
  }) => void
  isScheduling: boolean
}

export const AutoScheduleDialog = ({ open, onClose, onSubmit, isScheduling }: Props) => {
  const [mode, setMode] = useState<ScheduleMode>('unscheduled')
  const [customInstructions, setCustomInstructions] = useState('')

  const [rangeStart, setRangeStart] = useState(() => format(new Date(), "yyyy-MM-dd'T'HH:mm"))
  const [rangeEnd, setRangeEnd] = useState(() => {
    const defaultEndDate = new Date()
    defaultEndDate.setDate(defaultEndDate.getDate() + 7)
    return format(defaultEndDate, "yyyy-MM-dd'T'HH:mm")
  })

  if (!open) return null

  const handleSubmit = () => {
    const params: Parameters<typeof onSubmit>[0] = {
      mode,
      customInstructions: customInstructions.trim() || undefined,
    }
    if (mode === 'reschedule_range') {
      params.rangeStart = new Date(rangeStart).toISOString()
      params.rangeEnd = new Date(rangeEnd).toISOString()
    }
    onSubmit(params)
  }

  const modeOptions: { key: ScheduleMode; label: string; description: string }[] = [
    { key: 'unscheduled', label: '未配置のみ', description: '未配置のタスクだけを配置します' },
    { key: 'reschedule_all', label: 'すべてやり直し', description: '全タスクのスケジュールをリセットして再配置します' },
    {
      key: 'reschedule_range',
      label: '期間を指定して再配置',
      description: '指定した期間のタスクと過去の未消化タスクを再配置します',
    },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background rounded-lg p-5 w-[340px] shadow-xl space-y-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold">AI スケジュール</h3>

        {/* モード選択 */}
        <div className="space-y-2">
          {modeOptions.map((opt) => (
            <label
              key={opt.key}
              className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                mode === opt.key ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
              }`}
            >
              <input
                type="radio"
                name="schedule-mode"
                value={opt.key}
                checked={mode === opt.key}
                onChange={() => setMode(opt.key)}
                className="mt-0.5 accent-primary"
              />
              <div>
                <div className="text-sm font-medium">{opt.label}</div>
                <div className="text-xs text-muted-foreground">{opt.description}</div>
              </div>
            </label>
          ))}
        </div>

        {/* 期間選択 (reschedule_range のみ) */}
        {mode === 'reschedule_range' && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">配置期間</div>
            <div className="flex flex-col gap-2">
              <input
                type="datetime-local"
                className="w-full bg-muted rounded px-2 py-1.5 text-sm border border-border"
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
              />
              <div className="text-center text-muted-foreground text-sm">〜</div>
              <input
                type="datetime-local"
                className="w-full bg-muted rounded px-2 py-1.5 text-sm border border-border"
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* カスタム指示 */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-muted-foreground" htmlFor="custom-instructions">
            追加の指示（任意）
          </label>
          <Textarea
            id="custom-instructions"
            placeholder="例: 今日は早めに終わらせたい、コーディング系を優先して..."
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            rows={3}
            className="resize-none text-sm"
          />
        </div>

        {/* ボタン */}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} disabled={isScheduling}>
            キャンセル
          </Button>
          <Button onClick={handleSubmit} disabled={isScheduling}>
            {isScheduling ? '配置中...' : '実行'}
          </Button>
        </div>
      </div>
    </div>
  )
}
